import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  UserPermission, 
  UserPermissionCreateDto, 
  UserPermissionUpdateDto,
  UserPermissionResponse,
  UserPermissionQuery,
  UserPermissionFilter,
  UserPermissionSort,
  UserManagementItem
} from "../../modules/shared/model/user-permissions.interfaces";
import { 
  getUserPermissions as getUserPermissionsAction,
  getUserPermissionById as getUserPermissionByIdAction,
  createUserPermission as createUserPermissionAction,
  updateUserPermission as updateUserPermissionAction,
  deleteUserPermission as deleteUserPermissionAction,
  getUsersForPermissions as getUsersForPermissionsAction
} from "../Actions/UserPermissionsActions";

// Async Thunks
export const fetchUserPermissions = createAsyncThunk(
  "userPermissions/fetchAll", 
  async (query: UserPermissionQuery | undefined, { rejectWithValue }) => {
    try { 
      return await getUserPermissionsAction(query); 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to fetch user permissions"); 
    }
  }
);

export const fetchUserPermissionById = createAsyncThunk(
  "userPermissions/fetchById", 
  async (id: string, { rejectWithValue }) => {
    try { 
      return await getUserPermissionByIdAction(id); 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to fetch user permission"); 
    }
  }
);

export const createUserPermissionThunk = createAsyncThunk(
  "userPermissions/create", 
  async (data: UserPermissionCreateDto, { rejectWithValue }) => {
    try { 
      return await createUserPermissionAction(data); 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to create user permission"); 
    }
  }
);

export const updateUserPermissionThunk = createAsyncThunk(
  "userPermissions/update", 
  async ({ id, data }: { id: string; data: UserPermissionUpdateDto }, { rejectWithValue }) => {
    try { 
      return await updateUserPermissionAction(id, data); 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to update user permission"); 
    }
  }
);

export const deleteUserPermissionThunk = createAsyncThunk(
  "userPermissions/delete", 
  async (id: string, { rejectWithValue }) => {
    try { 
      await deleteUserPermissionAction(id); 
      return id; 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to delete user permission"); 
    }
  }
);

export const fetchUsersForPermissions = createAsyncThunk(
  "userPermissions/fetchUsers", 
  async (_, { rejectWithValue }) => {
    try { 
      return await getUsersForPermissionsAction(); 
    } catch (e: any) { 
      return rejectWithValue(e?.message || "Failed to fetch users"); 
    }
  }
);

// State interface
interface UserPermissionsState {
  // User permissions data
  permissions: UserPermission[];
  currentPermission: UserPermission | null;
  permissionsLoading: boolean;
  permissionsError: string | null;
  
  // Pagination and filtering
  totalCount: number;
  currentPage: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  searchQuery: string;
  
  // Filters and sorting
  filters: UserPermissionFilter;
  sort: UserPermissionSort;
  
  // Users for dropdown
  users: UserManagementItem[];
  usersLoading: boolean;
  usersError: string | null;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
  
  // UI state
  selectedPermissionId: string | null;
  showCreateModal: boolean;
  showEditModal: boolean;
  showDeleteModal: boolean;
}

const initialState: UserPermissionsState = {
  // Permissions data
  permissions: [],
  currentPermission: null,
  permissionsLoading: false,
  permissionsError: null,
  
  // Pagination
  totalCount: 0,
  currentPage: 1,
  limit: 10,
  hasNextPage: false,
  hasPreviousPage: false,
  searchQuery: "",
  
  // Filters and sorting
  filters: {},
  sort: { field: "createdAt", direction: "desc" },
  
  // Users
  users: [],
  usersLoading: false,
  usersError: null,
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
  
  // UI state
  selectedPermissionId: null,
  showCreateModal: false,
  showEditModal: false,
  showDeleteModal: false,
};

const userPermissionsSlice = createSlice({
  name: "userPermissions",
  initialState,
  reducers: {
    clearPermissionsError(state) { 
      state.permissionsError = null; 
    },
    clearCurrentPermission(state) { 
      state.currentPermission = null; 
    },
    setUserPermissionsSearchQuery(state, action: PayloadAction<string>) { 
      state.searchQuery = action.payload; 
    },
    setFilters(state, action: PayloadAction<UserPermissionFilter>) { 
      state.filters = { ...state.filters, ...action.payload }; 
    },
    setSort(state, action: PayloadAction<UserPermissionSort>) { 
      state.sort = action.payload; 
    },
    setCurrentPage(state, action: PayloadAction<number>) { 
      state.currentPage = action.payload; 
    },
    setUserPermissionsLimit(state, action: PayloadAction<number>) { 
      state.limit = action.payload; 
    },
    setFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllPermissions(state) {
      state.permissions = [];
      state.currentPermission = null;
      state.totalCount = 0;
    },
    
    // UI state management
    setSelectedPermissionId(state, action: PayloadAction<string | null>) {
      state.selectedPermissionId = action.payload;
    },
    setShowCreateModal(state, action: PayloadAction<boolean>) {
      state.showCreateModal = action.payload;
    },
    setShowEditModal(state, action: PayloadAction<boolean>) {
      state.showEditModal = action.payload;
    },
    setShowDeleteModal(state, action: PayloadAction<boolean>) {
      state.showDeleteModal = action.payload;
    },
    
    // Reset filters
    resetFilters(state) {
      state.filters = {};
      state.searchQuery = "";
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Fetch Permissions Extra Reducers
    builder
      .addCase(fetchUserPermissions.pending, (state) => { 
        state.permissionsLoading = true; 
        state.permissionsError = null; 
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action: PayloadAction<UserPermissionResponse>) => { 
        state.permissionsLoading = false; 
        state.permissions = action.payload.data || []; 
        state.totalCount = action.payload.count || 0; 
        state.hasNextPage = action.payload.hasNextPage || false;
      })
      .addCase(fetchUserPermissions.rejected, (state, action: any) => { 
        state.permissionsLoading = false; 
        state.permissionsError = action.payload as string; 
      });

    // Fetch Permission by ID Extra Reducers
    builder
      .addCase(fetchUserPermissionById.pending, (state) => { 
        state.permissionsLoading = true; 
        state.permissionsError = null; 
      })
      .addCase(fetchUserPermissionById.fulfilled, (state, action: PayloadAction<UserPermission>) => { 
        state.permissionsLoading = false; 
        state.currentPermission = action.payload; 
      })
      .addCase(fetchUserPermissionById.rejected, (state, action: any) => { 
        state.permissionsLoading = false; 
        state.permissionsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createUserPermissionThunk.pending, (state) => { 
        state.permissionsLoading = true; 
        state.permissionsError = null; 
      })
      .addCase(createUserPermissionThunk.fulfilled, (state, action: PayloadAction<UserPermission>) => { 
        state.permissionsLoading = false; 
        state.permissions.unshift(action.payload); 
        state.totalCount = state.totalCount + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
        state.showCreateModal = false;
      })
      .addCase(createUserPermissionThunk.rejected, (state, action: any) => { 
        state.permissionsLoading = false; 
        state.permissionsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateUserPermissionThunk.pending, (state) => { 
        state.permissionsLoading = true; 
        state.permissionsError = null; 
      })
      .addCase(updateUserPermissionThunk.fulfilled, (state, action: PayloadAction<UserPermission>) => {
        state.permissionsLoading = false;
        const index = state.permissions.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.permissions[index] = action.payload;
        if (state.currentPermission?.id === action.payload.id) state.currentPermission = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
        state.showEditModal = false;
      })
      .addCase(updateUserPermissionThunk.rejected, (state, action: any) => { 
        state.permissionsLoading = false; 
        state.permissionsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteUserPermissionThunk.pending, (state) => { 
        state.permissionsLoading = true; 
        state.permissionsError = null; 
      })
      .addCase(deleteUserPermissionThunk.fulfilled, (state, action: PayloadAction<string>) => { 
        state.permissionsLoading = false; 
        state.permissions = state.permissions.filter(x => x.id !== action.payload); 
        if (state.currentPermission?.id === action.payload) state.currentPermission = null; 
        state.totalCount = Math.max(0, state.totalCount - 1); 
        state.showDeleteModal = false;
        state.selectedPermissionId = null;
      })
      .addCase(deleteUserPermissionThunk.rejected, (state, action: any) => { 
        state.permissionsLoading = false; 
        state.permissionsError = action.payload as string; 
      });

    // Fetch Users Extra Reducers
    builder
      .addCase(fetchUsersForPermissions.pending, (state) => { 
        state.usersLoading = true; 
        state.usersError = null; 
      })
      .addCase(fetchUsersForPermissions.fulfilled, (state, action: PayloadAction<any[]>) => { 
        state.usersLoading = false; 
        state.users = action.payload; 
      })
      .addCase(fetchUsersForPermissions.rejected, (state, action: any) => { 
        state.usersLoading = false; 
        state.usersError = action.payload as string; 
      });
  }
});

export const { 
  clearPermissionsError,
  clearCurrentPermission, 
  setUserPermissionsSearchQuery,
  setFilters,
  setSort,
  setCurrentPage,
  setUserPermissionsLimit,
  setFormDirty,
  clearAllPermissions,
  setSelectedPermissionId,
  setShowCreateModal,
  setShowEditModal,
  setShowDeleteModal,
  resetFilters
} = userPermissionsSlice.actions;

export default userPermissionsSlice.reducer;
